import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dumbbell, Fingerprint, ArrowRight } from "lucide-react";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { BiometricService } from "../services/biometric.service";

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState("");
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "client", trainer_code: "" });
  // Profil założony przez trenera: osoba przejmuje go kodem, zamiast zakładać
  // drugie konto i zostawiać swoje pomiary po tamtej stronie.
  const [claimMode, setClaimMode] = useState(false);
  const [claimForm, setClaimForm] = useState({ code: "", email: "", password: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Gość jest teraz głównym wejściem — logowanie/rejestracja chowa się za
  // linkiem, żeby nie stały dwa równorzędne bloki obok siebie na starcie.
  const [showAuthForm, setShowAuthForm] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check if biometric login is available
  useEffect(() => {
    BiometricService.isAvailable().then(available => {
      if (available) {
        setBiometricAvailable(true);
        BiometricService.getBiometryLabel().then(setBiometricLabel);
      }
    });
  }, []);

  const handleBiometricLogin = async () => {
    setLoading(true);
    try {
      const storedToken = await BiometricService.authenticate();
      if (storedToken) {
        // Verify token is still valid
        const res = await api("get", "/auth/me", null, storedToken);
        login(storedToken, res.data);
        toast.success("Zalogowano biometrycznie");
        navigate(res.data.role === "trainer" ? "/trainer" : "/client");
      } else {
        toast.error("Logowanie biometryczne nie powiodło się");
      }
    } catch {
      toast.error("Token wygasł — zaloguj się hasłem");
      await BiometricService.clearEnrolledToken();
      setBiometricAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  // Wejście bez rejestracji. Rola jest wymagana, bo od niej zależy cały
  // interfejs — trener zapisuje cudze treningi, trenujący swoje — więc nie ma
  // tu jednego przycisku "wypróbuj", tylko dwa.
  const startAsGuest = async (role) => {
    setLoading(true);
    try {
      const res = await api("post", "/auth/guest", { role });
      login(res.data.access_token, res.data.user);
      toast.success("Możesz zacząć. Konto założysz później, dane zostaną.");
      navigate(role === "trainer" ? "/trainer" : "/client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się zacząć");
    } finally {
      setLoading(false);
    }
  };

  const handleClaimProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api("post", "/auth/claim-profile", {
        code: claimForm.code.trim().toUpperCase(),
        email: claimForm.email,
        password: claimForm.password,
      });
      login(res.data.access_token, res.data.user);
      toast.success("Profil jest Twój — wszystko, co trener zapisał, zostaje");
      navigate(res.data.user.role === "trainer" ? "/trainer" : "/client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się przejąć profilu");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api("post", "/auth/forgot-password", { email: forgotEmail });
      toast.success("Sprawdź email — wysłaliśmy link do resetowania hasła");
      setForgotMode(false);
      setForgotEmail("");
    } catch (err) {
      toast.error("Nie udało się wysłać emaila");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && !acceptedTerms) {
      toast.error("Musisz zaakceptować regulamin i politykę prywatności");
      return;
    }
    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email: form.email, password: form.password } : form;
      const res = await api("post", endpoint, payload);
      login(res.data.access_token, res.data.user);
      toast.success(isLogin ? "Welcome back" : "Account created");
      navigate(res.data.user.role === "trainer" ? "/trainer" : "/client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-brand">
          <div className="brand-icon">
            <Dumbbell size={28} strokeWidth={1.5} />
          </div>
          <h1>DuoFit</h1>
        </div>

        <Card className="auth-card" data-testid="guest-card" style={{ marginBottom: 16 }}>
          <CardContent style={{ padding: 20 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Zacznij bez zakładania konta</div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 14 }}>
              Zapisuj treningi i pomiary od razu. Konto założysz później — dane zostaną.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button type="button" variant="outline" disabled={loading} style={{ flex: 1, gap: 6 }}
                      data-testid="guest-trainer-btn" onClick={() => startAsGuest("trainer")}>
                Jestem trenerem <ArrowRight size={15} />
              </Button>
              <Button type="button" variant="outline" disabled={loading} style={{ flex: 1, gap: 6 }}
                      data-testid="guest-client-btn" onClick={() => startAsGuest("client")}>
                Trenuję <ArrowRight size={15} />
              </Button>
            </div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 12, lineHeight: 1.5 }}>
              Zaczynając, akceptujesz{" "}
              <a href="/legal/terms" target="_blank" rel="noopener" style={{ color: "inherit", textDecoration: "underline" }}>Regulamin</a>{" "}
              i{" "}
              <a href="/legal/privacy" target="_blank" rel="noopener" style={{ color: "inherit", textDecoration: "underline" }}>Politykę Prywatności</a>.
              Dostęp do konta tymczasowego masz tylko z tej przeglądarki.
            </div>
          </CardContent>
        </Card>

        {!showAuthForm && (
          <button
            type="button"
            onClick={() => setShowAuthForm(true)}
            data-testid="show-auth-form-link"
            style={{ display: "block", margin: "4px auto 0", background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "13px", textDecoration: "underline" }}
          >
            Masz już konto? Zaloguj się
          </button>
        )}

        {showAuthForm && (
        <Card className="auth-card" data-testid="auth-card">
          <Tabs value={isLogin ? "login" : "register"} onValueChange={(v) => setIsLogin(v === "login")}>
            <TabsList className="auth-tabs">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Sign Up</TabsTrigger>
            </TabsList>

            <CardContent className="auth-content">
              <form onSubmit={handleSubmit}>
                {!isLogin && (
                  <div className="field">
                    <Label>Full Name</Label>
                    <Input data-testid="name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required={!isLogin} />
                  </div>
                )}

                <div className="field">
                  <Label>Email</Label>
                  <Input type="email" data-testid="email-input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
                </div>

                <div className="field">
                  <Label>Password</Label>
                  <Input type="password" data-testid="password-input" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required />
                </div>

                {!isLogin && (
                  <>
                    <div className="field">
                      <Label>I am a</Label>
                      <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                        <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="client">Client</SelectItem>
                          <SelectItem value="trainer">Trainer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.role === "client" && (
                      <div className="field">
                        <Label>Trainer Code <span className="optional">(optional)</span></Label>
                        <Input data-testid="trainer-code-input" value={form.trainer_code} onChange={(e) => setForm({...form, trainer_code: e.target.value.toUpperCase()})} placeholder="ABC12345" />
                      </div>
                    )}
                  </>
                )}

                {!isLogin && (
                  <div className="field" style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, lineHeight: 1.4, marginTop: 12, marginBottom: 4 }}>
                    <input
                      id="accept-terms"
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      required={!isLogin}
                      data-testid="accept-terms-checkbox"
                      style={{ marginTop: 3, flexShrink: 0 }}
                    />
                    <label htmlFor="accept-terms" style={{ color: "var(--text-secondary)", cursor: "pointer" }}>
                      Akceptuję{" "}
                      <a href="/legal/terms" target="_blank" rel="noopener" style={{ color: "var(--text-primary)", textDecoration: "underline" }}>Regulamin</a>{" "}
                      i{" "}
                      <a href="/legal/privacy" target="_blank" rel="noopener" style={{ color: "var(--text-primary)", textDecoration: "underline" }}>Politykę Prywatności</a>
                      {form.role === "client" && (
                        <> oraz wyrażam zgodę na przetwarzanie moich danych zdrowotnych w celu świadczenia usług trenerskich (art. 9 ust. 2 lit. a RODO).</>
                      )}
                    </label>
                  </div>
                )}

                <Button type="submit" className="submit-btn" disabled={loading || (!isLogin && !acceptedTerms)} data-testid="auth-submit-btn">
                  {loading ? "Please wait..." : (isLogin ? "Sign In" : "Create Account")}
                </Button>

                {isLogin && biometricAvailable && (
                  <Button
                    type="button"
                    variant="outline"
                    className="biometric-btn"
                    onClick={handleBiometricLogin}
                    disabled={loading}
                    style={{ width: "100%", marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                  >
                    <Fingerprint size={18} />
                    {biometricLabel || "Biometria"}
                  </Button>
                )}

                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setClaimMode(true)}
                    data-testid="claim-profile-link"
                    style={{ display: "block", margin: "12px auto 0", background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "13px", textDecoration: "underline" }}
                  >
                    Mam kod od trenera
                  </button>
                )}

                {isLogin && (
                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={() => setForgotMode(true)}
                    style={{ display: "block", margin: "12px auto 0", background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "13px", textDecoration: "underline" }}
                  >
                    Nie pamiętam hasła
                  </button>
                )}
              </form>
            </CardContent>
          </Tabs>
        </Card>
        )}

        {/* Przejęcie profilu założonego przez trenera */}
        {claimMode && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}
               onClick={() => setClaimMode(false)}>
            <Card style={{ width: "100%", maxWidth: 400, margin: 16 }} onClick={e => e.stopPropagation()}>
              <CardContent style={{ padding: 24 }}>
                <h3 style={{ marginBottom: 8 }}>Przejmij swój profil</h3>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16, lineHeight: 1.5 }}>
                  Trener założył Ci profil i zapisywał w nim treningi. Kod od niego
                  robi ten profil Twoim — nic nie jest przenoszone ani tracone.
                </p>
                <form onSubmit={handleClaimProfile}>
                  <div className="field">
                    <Label>Kod od trenera</Label>
                    <Input data-testid="claim-code-input" value={claimForm.code} required
                           placeholder="ABCD234567"
                           onChange={e => setClaimForm({ ...claimForm, code: e.target.value.toUpperCase() })} />
                  </div>
                  <div className="field">
                    <Label>Email</Label>
                    <Input type="email" required data-testid="claim-profile-email" value={claimForm.email}
                           onChange={e => setClaimForm({ ...claimForm, email: e.target.value })} />
                  </div>
                  <div className="field">
                    <Label>Hasło</Label>
                    <Input type="password" required data-testid="claim-profile-password" value={claimForm.password}
                           onChange={e => setClaimForm({ ...claimForm, password: e.target.value })} />
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                    <Button type="button" variant="outline" onClick={() => setClaimMode(false)} style={{ flex: 1 }}>Anuluj</Button>
                    <Button type="submit" disabled={loading} style={{ flex: 1 }} data-testid="claim-profile-submit">
                      {loading ? "Przejmuję..." : "Przejmij profil"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Forgot password modal */}
        {forgotMode && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}
               onClick={() => setForgotMode(false)}>
            <Card style={{ width: "100%", maxWidth: 400, margin: 16 }} onClick={e => e.stopPropagation()}>
              <CardContent style={{ padding: 24 }}>
                <h3 style={{ marginBottom: 16 }}>Resetowanie hasła</h3>
                <form onSubmit={handleForgotPassword}>
                  <div className="field">
                    <Label>Email</Label>
                    <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required placeholder="twoj@email.com" />
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                    <Button type="button" variant="outline" onClick={() => setForgotMode(false)} style={{ flex: 1 }}>Anuluj</Button>
                    <Button type="submit" disabled={loading} style={{ flex: 1 }}>{loading ? "Wysyłam..." : "Wyślij link"}</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        <footer style={{ marginTop: 24, fontSize: 12, color: "var(--text-secondary)", textAlign: "center", lineHeight: 1.8 }}>
          <a href="/legal/terms" style={{ color: "inherit", textDecoration: "underline" }}>Regulamin</a>
          {" · "}
          <a href="/legal/privacy" style={{ color: "inherit", textDecoration: "underline" }}>Polityka Prywatności</a>
          {" · "}
          <a href="/legal/cookies" style={{ color: "inherit", textDecoration: "underline" }}>Cookies</a>
          {" · "}
          <a href="/legal/dpa" style={{ color: "inherit", textDecoration: "underline" }}>DPA</a>
          {" · "}
          <a href="/legal/ai-note" style={{ color: "inherit", textDecoration: "underline" }}>AI</a>
        </footer>
      </div>
    </div>
  );
};

export default AuthPage;
